// apps/client/src/game/spaces.ts — contract for the world/spaces registry (one file per SpaceKind, §8.4) and the scene switcher.
// Owners: out → M0-T18 (wraps M0-T13's OutdoorScene), shop → M1-T14, room → M1-T16, museum → M2-T10, vroom → M2-T11.
// Until a kind's file exists, a plain cream room stands in so `space.enter` still works.
import { tuning } from '@whiskerton/content';
import type { MoveState, Rect, SpaceId, SpaceKind, SpaceSnapshot, Vec2 } from '@whiskerton/shared';
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { toyMaterial } from '../characters/doll';
import { spaceModules } from '../world/spaces.gen';
import type { Game } from './game';

export interface SpaceScene {
  readonly scene: THREE.Scene; readonly bounds: Rect; heightAt(p: Vec2): number; walkable(p: Vec2): boolean; dispose(): void;
  /** rev 5 item 12: per-frame work (curator, aquarium fish, a villager in their room). dt in seconds (Frame.dt), nowMs = Frame.now.
   *  Called by client:game/systems/spaces.ts (M2-T10, ExtSystem slot 'animation'). */
  update?(dt: number, nowMs: number): void;
  /** rev 7: called every frame by M0-T18's render system with the town clock (the outdoor scene lights itself). */
  tick?(dt: number, minuteOfDay: number): void;
  /** rev 7: the walker asks whether a step is allowed (outdoors: walkability + tier/ramp rules); falls back to walkable(to). */
  canMove?(from: Vec2, to: Vec2): boolean;
}
export interface SpaceSceneDef {
  kind: SpaceKind;
  build(snap: SpaceSnapshot, game: Game): Promise<SpaceScene>;
  apply?(snap: SpaceSnapshot, scene: SpaceScene, game: Game): void;   // re-entry / resync without a rebuild
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const defs = (): readonly SpaceSceneDef[] => (spaceModules as readonly { space: SpaceSceneDef }[]).map((m) => m.space);

export class SpaceManager {
  current: SpaceScene | null = null;
  private kind: SpaceKind | null = null;
  private spaceId: SpaceId | null = null;
  private readonly game: Game;
  private fadeEl: HTMLDivElement | null = null;
  private run = 0;
  /** Bumped by reset() (leaving the town); a build that finishes after it is thrown away whatever its kind. */
  private epoch = 0;
  /** The outdoor SpaceScene of this visit, kept while you are indoors and disposed on reset(). */
  private outdoor: SpaceScene | null = null;
  constructor(game: Game) { this.game = game; }                 // DOM-free, so `new Game()` stays DOM-free
  private get fade(): HTMLDivElement {
    if (!this.fadeEl) {
      this.fadeEl = document.createElement('div');
      this.fadeEl.style.cssText = `position:fixed;inset:0;background:#FFFBF1;opacity:0;pointer-events:none;transition:opacity ${tuning.camera.doorFadeMs}ms ease;z-index:5`;
    }
    return this.fadeEl;
  }
  get space(): SpaceId | null { return this.spaceId; }
  /** Switches to `snap`'s space: a door fade when the space changes, the outdoor scene kept and re-applied, then you at `spawn` and
   *  everyone else from `snap.occupants`. Runs the net handlers' `onSpace` every time; emits bus `space:changed` only on a scene
   *  switch (the first scene of a visit, or a different space), never on a resync of the space you are already in. */
  async switchTo(snap: SpaceSnapshot, spawn: MoveState): Promise<void> {
    const run = ++this.run, epoch = this.epoch;
    const from = this.spaceId ?? snap.space;
    const changing = this.current !== null && from !== snap.space;
    const switched = this.current === null || from !== snap.space;
    if (!this.fade.isConnected) document.body.append(this.fade);
    try {
      if (changing) { this.fade.style.opacity = '1'; await sleep(tuning.camera.doorFadeMs); }
      if (run !== this.run) return;                               // a newer switch took over during the fade
      const def = defs().find((d) => d.kind === snap.kind);
      if (this.current && this.kind === snap.kind && snap.kind === 'out' && def?.apply) def.apply(snap, this.current, this.game);
      else {
        const next = def ? await def.build(snap, this.game) : fallbackRoom(snap);
        if (run !== this.run) {                                   // superseded: a room is dropped; the outdoor scene only after a reset
          if (snap.kind !== 'out' || epoch !== this.epoch) next.dispose();
          return;
        }
        if (this.current && this.kind !== 'out') this.current.dispose();        // the outdoor scene is kept and reused
        this.current = next;
        this.kind = snap.kind;
        if (snap.kind === 'out') this.outdoor = next;
      }
    } catch (e) {
      if (run === this.run) this.fade.style.opacity = '0';        // a failed build never leaves the cream fade over the screen
      throw e;
    }
    this.spaceId = snap.space;
    this.game.enteredSpace(snap, spawn);
    this.fade.style.opacity = '0';
    if (switched) this.game.bus.emit('space:changed', { from, to: snap.space });
  }
  /** Leaving the town: drop every scene (the outdoor one too: its dispose forgets this town, so the next town rebuilds into the
   *  same OutdoorScene, whose build() frees the old island) and the fade element. */
  reset(): void {
    this.run++; this.epoch++;
    if (this.current && this.kind !== 'out') this.current.dispose();
    this.outdoor?.dispose();
    this.outdoor = null;
    this.current = null; this.kind = null; this.spaceId = null;
    this.fadeEl?.remove();
    this.fadeEl = null;
  }
}

/** A plain cream room so `space.enter` works for kinds whose scene lands later (shop M1-T14, museum M2-T10). */
export function fallbackRoom(snap: SpaceSnapshot): SpaceScene {
  const size = snap.kind === 'shop' ? tuning.spaces.shop : snap.kind === 'museum' ? tuning.spaces.museum : snap.kind === 'room' ? snap.room : { w: 6, h: 6 };
  const { w, h } = size;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#F3E4C6');
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(w, h).rotateX(-Math.PI / 2), toyMaterial('#C99C6C'));
  floor.position.set(w / 2, 0, h / 2);
  floor.receiveShadow = true;
  const wallMat = toyMaterial('#F1E4CB'), skirt = toyMaterial('#FFF8EC');
  const back = new THREE.Mesh(new RoundedBoxGeometry(w + 0.3, 3.3, 0.3, 3, 0.1), wallMat);
  back.position.set(w / 2, 1.65, -0.15);
  const left = new THREE.Mesh(new RoundedBoxGeometry(0.3, 3.3, h + 0.3, 3, 0.1), wallMat);
  left.position.set(-0.15, 1.65, h / 2);
  const s1 = new THREE.Mesh(new RoundedBoxGeometry(w, 0.16, 0.08, 2, 0.03), skirt);
  s1.position.set(w / 2, 0.08, 0.04);
  const s2 = new THREE.Mesh(new RoundedBoxGeometry(0.08, 0.16, h, 2, 0.03), skirt);
  s2.position.set(0.04, 0.08, h / 2);
  const I = tuning.light.interior.day;                            // the rooms' daytime light (world/interior.ts)
  const key = new THREE.DirectionalLight(I.key, I.keyIntensity);
  key.position.set(w * 0.3, 6, h + 3);
  scene.add(floor, back, left, s1, s2, new THREE.HemisphereLight(I.hemiSky, I.hemiGround, I.hemi), key, key.target);
  key.target.position.set(w / 2, 0, h / 2);
  const inside = (p: Vec2) => p.x >= 0.3 && p.y >= 0.3 && p.x <= w - 0.3 && p.y <= h - 0.3;
  return { scene, bounds: { x: 0, y: 0, w, h }, heightAt: () => 0, walkable: inside, canMove: (_a, b) => inside(b),
    dispose: () => scene.traverse((o) => { const m = o as THREE.Mesh; if (m.isMesh) m.geometry.dispose(); }) };
}
